import { Headline, IconButton, Text } from '@oneaudi/unified-web-alpha-components';
import { useSetAtom } from 'jotai/index';
import { useCallback, useEffect, useState } from 'react';
import { SummaryCtaButton } from '~/app/compositions/content/service-footer/components/user-marketing/main-cta-button';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketUiConfig } from '~/app/market-config/use-market-ui-config';
import {
	ButtonGroupStyles,
	CalcBackStyles,
	CarlineStyles,
	SummaryHeaderPriceBarStyle,
} from '~/app/pages/summary/components/summary-header-price-bar/summary-header-price-bar-style';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { EventAction, NbaTargetTypes } from '~/app/tracking/feld-m-types';
import { useConfigurator } from '~/graphql/hooks/use-configurator';
import { useBreakPoint } from '~/hooks/responsive/useBreakPoint';
import { state } from '~/storage';
import { DefaultBar } from '~/styles/default-bar';
import { DATA_TEST_ID } from '~/test/data-test-id';

export function SummaryHeaderPriceBar({ id }: { id?: string }) {
	const setCurrentView = useSetAtom(state.currentView);
	const marketUi = useMarketUiConfig();
	const [isScrolled, setIsScrolled] = useState(false);
	const { isLBreakpoint } = useBreakPoint();
	const { i18n } = useI18n();
	const onScroll = () => {
		if (window.scrollY > 0) {
			setIsScrolled(true);
		} else {
			setIsScrolled(false);
		}
	};

	useEffect(() => {
		window.addEventListener('scroll', onScroll);
		return () => window.removeEventListener('scroll', onScroll);
	}, []);

	const trackClickEvent = useCallback(
		(eventName: string, componentName: string, elementName: string, label?: string, url?: string) => {
			appTrackingService.summaryClickEvent({
				eventAction: EventAction.InternalLink,
				eventName: `configurator (summary) - click to ${eventName}`,
				componentName,
				label,
				targetURL: url,
				elementName,
			});
		},
		[],
	);

	const trackSummaryCtaClickEvent = useCallback((nbaName: NbaTargetTypes, label: string, url?: string) => {
		appTrackingService.summaryClickEvent({
			eventAction: nbaName === NbaTargetTypes.PDF ? EventAction.Download : EventAction.Content,
			eventName: `configurator (price bar) - click on ${nbaName}`,
			componentName: 'configurator-price-bar',
			label,
			targetURL: url,
			elementName: 'button',
			value: nbaName === NbaTargetTypes.PDF ? 'PDF' : '',
		});
	}, []);

	const onBackButtonClick = useCallback(() => {
		setCurrentView('configurator');
		trackClickEvent(
			'back to configuration page',
			'configurator-summary',
			'icon',
			`${window.location.href.replace('summary', 'configurator')}`,
		);
	}, [setCurrentView, trackClickEvent]);

	if (!marketUi) return <></>;

	return (
		<SummaryHeaderPriceBarStyle
			id={id}
			tabIndex={0}
			data-testid={DATA_TEST_ID.PRICE_BAR_CONTAINER}
			$isScrolled={isScrolled}>
			<div className="bar-container">
				<DefaultBar $isScrolled={isScrolled} className="default-bar">
					{!isLBreakpoint && <Carline isScrolled={isScrolled} />}
					<div className={'calc-sum'}>
						<CalcBackStyles $isScrolled={isScrolled}>
							{isScrolled && (
								<IconButton
									href={'#configurator'}
									variant={'tertiary'}
									icon={'back'}
									className="back__button__tertiary"
									onClick={onBackButtonClick}
									aria-label={i18n('ngc.actions.changeConfiguration')}
									data-testid={DATA_TEST_ID.PRICE_BAR_BACK_BUTTON}
									size="md"
								/>
							)}
							{!isScrolled && isLBreakpoint && <Carline isScrolled={isScrolled} />}
							{}
							<div className="info-block" tabIndex={0}>
								{marketUi.calculationInfoComponent}
							</div>
						</CalcBackStyles>
						<ButtonGroupStyles $isScrolled={isScrolled}>
							{isScrolled && (
								<IconButton
									href={'#configurator'}
									variant={'secondary'}
									icon={'back'}
									className="back__button"
									onClick={onBackButtonClick}
									aria-label={i18n('ngc.actions.changeConfiguration')}
									data-testid={DATA_TEST_ID.PRICE_BAR_BACK_BUTTON}
									size="sm"
								/>
							)}
							<SummaryCtaButton
								variant="primary"
								size="sm"
								isSecondCta="false"
								trackClickEvent={trackSummaryCtaClickEvent}
							/>
							<SummaryCtaButton
								variant="secondary"
								size="sm"
								isSecondCta="true"
								trackClickEvent={trackSummaryCtaClickEvent}
							/>
						</ButtonGroupStyles>
					</div>
				</DefaultBar>
			</div>
		</SummaryHeaderPriceBarStyle>
	);
}

const Carline = ({ isScrolled }: { isScrolled: boolean }) => {
	const {
		catalog: { carInfo },
	} = useConfigurator();
	const carline = carInfo?.carline ?? 'Carline';
	const model = carInfo?.model ?? 'Model';
	const { i18n } = useI18n();
	const setCurrentView = useSetAtom(state.currentView);

	return (
		<CarlineStyles $isScrolled={isScrolled}>
			<IconButton
				href={'#configurator'}
				variant={'tertiary'}
				icon={'back'}
				className="back__button"
				onClick={() => {
					setCurrentView('configurator');
					appTrackingService.summaryClickEvent({
						eventAction: EventAction.InternalLink,
						eventName: `configurator (summary) - click to back to configuration page`,
						componentName: 'configurator-summary',
						targetURL: `${window.location.href.replace('summary', 'configurator')}`,
						elementName: 'icon',
					});
				}}
				aria-label={i18n('ngc.actions.changeConfiguration')}
				data-testid={DATA_TEST_ID.PRICE_BAR_BACK_BUTTON}
				size="md"
			/>
			{}
			<div className="headline-container" data-testid={DATA_TEST_ID.CAR_TITLE} tabIndex={0}>
				<Headline
					tag={'h1'}
					variant={{ type: 'headline', order: '4', weight: 'normal' }}
					className="headline__main">
					{carline}
				</Headline>
				<Text tag={'p'} variant={{ type: 'copy', order: '1', style: 'normal' }} className="headline__sub">
					{model}
				</Text>
			</div>
		</CarlineStyles>
	);
};
